import { format, parse } from "date-fns";
import { useNavigate } from "react-router";

import type { AssetBookingDetailDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { EntityDetailsCard } from "@/components/EntityDetailsCard/EntityDetailsCard";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Pdf } from "@/components/Pdf/Pdf";
import { Stack } from "@/design-system/Stack";
import { timeWithoutSeconds } from "@/helpers/date";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface ReservationDetailsProps {
  reservation: AssetBookingDetailDto;
  onDelete: () => Promise<void>;
}
export function ReservationDetails({ reservation, onDelete }: ReservationDetailsProps): React.ReactNode {
  const slug = useSlug();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-reservation-modal");
  const isCancelled = !!reservation.cancelledAt;

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      callbackFn: () =>
        void navigate(
          routes.bookings.editBooking({
            slug,
            aid: reservation.asset.id,
            bid: reservation.id,
          }),
        ),
      isHidden: !reservation.canEdit || isCancelled,
    },
    {
      labels: {
        default: t("common.action.cancel"),
      },
      value: "cancel",
      icon: <Trash02Icon />,
      callbackFn: () => openDeleteModal(reservation.id),
      isHidden: !reservation.canDelete || isCancelled,
    },
  ];

  function getCancellationReason(): string {
    switch (reservation.cancellationReason) {
      case "cancelled":
        return reservation.cancellationReasonDescription ?? t("page.bookings.reservation-details.cancelled.cancelled");
      case "assetDeleted":
        return t("page.bookings.reservation-details.cancelled.deleted");
      case "assetUnpublished":
        return t("page.bookings.reservation-details.cancelled.unpublished");
      case "assetUpdated":
        return t("page.bookings.reservation-details.cancelled.updated");
      case "cancelledExternally":
        return t("page.bookings.reservation-details.cancelled.externally");
      default:
        return "";
    }
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={`${reservation.asset.name.trim() || t("page.bookings.reservation-details.title")} | ${formatDate(i18n, "date", reservation.date)} ${timeWithoutSeconds(reservation.startTime)}`}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.bookings.reservation-details.breadcrumbs.calendar.title"),
              to: `${routes.calendar.list({
                slug,
              })}?tab=reservations`,
            },
            {
              name: t("page.bookings.reservation-details.breadcrumbs.reservation.title"),
            },
          ]}
        />
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="50%">
          <EntityDetailsCard
            title={reservation.asset.name}
            description={reservation.asset.description}
            images={reservation.asset.images}
            actions={menuActions}
            renderBadges={
              isCancelled
                ? () => (
                    <span className="w-fit rounded-lg bg-background-danger-secondary px-space-sm py-space-xs">
                      <span className="text-caption-emphasis text-text-danger-tertiary">
                        {t("page.bookings.reservations.reservation-cancelled")}
                      </span>
                    </span>
                  )
                : undefined
            }
            infoItems={[
              {
                icon: <CalendarIcon />,
                label: t("page.bookings.reservations.label.datetime"),
                value: `${format(parse(reservation.date, "yyyy-mm-dd", new Date(0)), "dd")} ${formatDate(i18n, "month", parse(reservation.date, "yyyy-MM-dd", new Date(0)))} ${timeWithoutSeconds(reservation.startTime)} - ${timeWithoutSeconds(reservation.endTime)}`,
              },
              {
                icon: <MarkerPin01Icon />,
                label: t("page.bookings.reservations.label.location"),
                value: reservation.asset.locationSpecification,
              },
            ]}
            renderBody={isCancelled ? () => <span className="text-body">{getCancellationReason()}</span> : undefined}
            renderFooter={
              reservation.bookingReason || reservation.regulationDocument
                ? () => (
                    <Stack gap="md">
                      {reservation.bookingReason ? (
                        <section className="flex w-fit flex-col gap-space-sm">
                          <span className="text-body-emphasis">{t("page.bookings.reservation-details.reason")}</span>
                          <span className="text-body">{reservation.bookingReason}</span>
                        </section>
                      ) : null}
                      {reservation.regulationDocument ? (
                        <Stack gap="sm">
                          <span className="text-body-emphasis">
                            {t("page.bookings.reservation-details.regulations")}
                          </span>
                          <Pdf
                            fileName={reservation.regulationDocument.fileName}
                            onClick={() => window.open(reservation.regulationDocument?.url, "_blank")}
                          />
                        </Stack>
                      ) : null}
                    </Stack>
                  )
                : undefined
            }
          />
        </PageGrid.Column>
      </PageGrid.Row>
      <DeleteModal
        title={t("page.bookings.reservation-details.delete.modal.title")}
        description={t("page.bookings.reservation-details.delete.modal.description")}
        onDelete={onDelete}
        rejectBtnProps={{
          text: t("page.bookings.reservation-details.delete.modal.keep-reservation"),
          "data-testid": "modal-cancel-delete",
        }}
        deleteBtnProps={{
          text: t("page.bookings.reservation-details.delete.modal.cancel-reservation"),
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </DocumentPage>
  );
}
